import api from '../lib/axios';
import {
    PaymentMethod,
    Subscription,
    BillingAttempt,
    PaymentMethodType,
    SubscriptionPlanType
} from '../types/payments';
import { openEpaycoCheckout } from '../features/payments/paymentsApi';
import {
    getOrFetchAuthenticatedResource,
    invalidateAuthenticatedResource,
} from '../lib/authenticatedResource';

const BASE_URL = '/payments';

type FetchOpts = { force?: boolean };

function invalidatePaymentCheckoutResources(): void {
    invalidateAuthenticatedResource('payment-subscription');
    invalidateAuthenticatedResource('payment-methods');
    invalidateAuthenticatedResource('payment-billing-history');
}

export const recurringPaymentsApi = {
    getEpaycoPublicKey: async (): Promise<{ public_key: string; test: boolean }> => {
        const res = await api.get(`${BASE_URL}/epayco-public-key`);
        return res.data;
    },

    registerCard: async (data: {
        epayco_token: string;
        card_holder_name: string;
        identification_number: string;
        identification_type?: 'CC' | 'NIT' | 'PPN' | 'DNI';
        payment_method: PaymentMethodType;
        expiration_date: string;
        masked_number?: string;
        email?: string;
        /** Cardholder phone (Colombia flows send this; optional for legacy/intl). */
        phone?: string;
        city?: string;
        address?: string;
        set_as_default: boolean;
    }): Promise<PaymentMethod> => {
        const res = await api.post(`${BASE_URL}/methods`, data);
        invalidateAuthenticatedResource('payment-methods');
        return res.data;
    },

    listCards: async (opts?: FetchOpts): Promise<PaymentMethod[]> => {
        return getOrFetchAuthenticatedResource(
            'payment-methods',
            async () => {
                const res = await api.get(`${BASE_URL}/methods`);
                return res.data.methods || [];
            },
            opts
        );
    },

    deleteCard: async (methodId: string): Promise<void> => {
        await api.delete(`${BASE_URL}/methods/${methodId}`);
        invalidateAuthenticatedResource('payment-methods');
    },

    getSubscription: async (
        opts?: FetchOpts
    ): Promise<{
        subscription: Subscription | null;
        has_subscription: boolean;
        payment_result?: { code: string; message: string } | null;
    }> => {
        return getOrFetchAuthenticatedResource(
            'payment-subscription',
            async () => {
                const res = await api.get(`${BASE_URL}/subscription`);
                return res.data;
            },
            opts
        );
    },

    createSubscription: async (data: {
        plan_type: SubscriptionPlanType;
        payment_method_id: string;
    }): Promise<Subscription> => {
        const res = await api.post(`${BASE_URL}/subscribe`, data);
        invalidatePaymentCheckoutResources();
        return res.data;
    },

    cancelSubscription: async (): Promise<void> => {
        await api.delete(`${BASE_URL}/subscription`);
        invalidatePaymentCheckoutResources();
    },

    getUpgradePreview: async (newPlanType: string): Promise<{
        current_plan: string;
        new_plan: string;
        current_plan_label: string;
        new_plan_label: string;
        remaining_messages: number;
        price_per_message: number;
        credit_amount: number;
        new_plan_price: number;
        charge_today: number;
        currency?: string;
        new_messages: number;
        new_period_end: string;
    }> => {
        const res = await api.get(`${BASE_URL}/subscription/upgrade/preview?new_plan=${newPlanType}`);
        return res.data;
    },

    upgradeSubscription: async (
        newPlanType: string,
        deviceSessionId?: string
    ): Promise<{ subscription: Subscription; proration: object }> => {
        const res = await api.post(`${BASE_URL}/subscription/upgrade`, {
            new_plan_type: newPlanType,
            device_session_id: deviceSessionId ?? null,
        });
        invalidatePaymentCheckoutResources();
        return res.data;
    },

    getBillingHistory: async (
        limit: number = 20,
        opts?: FetchOpts
    ): Promise<{ attempts: BillingAttempt[]; total: number }> => {
        return getOrFetchAuthenticatedResource(
            'payment-billing-history',
            async () => {
                const res = await api.get(`${BASE_URL}/billing-history?limit=${limit}`);
                return { attempts: res.data.attempts || [], total: res.data.total || 0 };
            },
            opts
        );
    },

    payPse: async (data: {
        amount: number;
        plan_type: string;
        bank_code: string;
        document_type: string;
        document_number: string;
        payer_type: string;
    }): Promise<{ bank_url: string; state: string; transaction_id?: string }> => {
        const res = await api.post(`${BASE_URL}/pay-pse`, data);
        return res.data;
    },

    getPseBanks: async (): Promise<{ banks: { bankCode: string; bankName: string }[] }> => {
        const res = await api.get(`${BASE_URL}/pse-banks`);
        return res.data;
    },

    payBancolombia: async (data: {
        amount: number;
        plan_type: string;
        document_type?: string;
        document_number?: string;
    }): Promise<{ checkout?: any; open_checkout?: boolean; bank_url?: string; reference_code?: string }> => {
        const res = await api.post(`${BASE_URL}/pay-bancolombia`, data);
        const payload = res.data;
        if (payload.open_checkout && payload.checkout) {
            await openEpaycoCheckout(payload.checkout);
        }
        return payload;
    },

    getGeoPricing: async (countryOverride?: string, opts?: FetchOpts): Promise<any> => {
        const headers = countryOverride ? { 'X-Country-Code': countryOverride } : {};
        // Country overrides are QA-only; skip the shared session cache.
        if (countryOverride) {
            const res = await api.get(`/international-payments/pricing`, { headers });
            return res.data;
        }
        return getOrFetchAuthenticatedResource(
            'international-pricing',
            async () => {
                const res = await api.get(`/international-payments/pricing`);
                return res.data;
            },
            opts
        );
    },

    initiateInternationalPayment: async (data: {
        amount: number;
        plan_type: string;
        buyer_full_name?: string;
    }, countryOverride?: string): Promise<any> => {
        const headers = countryOverride ? { 'X-Country-Code': countryOverride } : {};
        const res = await api.post(`/international-payments/initiate`, data, { headers });
        return res.data;
    },

    createInternationalSubscription: async (data: {
        plan_type: SubscriptionPlanType;
        payment_method_id: string;
        device_session_id?: string;
    }, countryOverride?: string): Promise<Subscription> => {
        const headers = countryOverride ? { 'X-Country-Code': countryOverride } : {};
        const res = await api.post(`/international-payments/subscribe`, data, { headers });
        invalidatePaymentCheckoutResources();
        return res.data;
    },

    chargeInternational: async (data: {
        amount: number;
        plan_type: string;
    }, countryOverride?: string): Promise<{ success: boolean; transaction_id?: string }> => {
        const headers = countryOverride ? { 'X-Country-Code': countryOverride } : {};
        const res = await api.post(`/international-payments/charge`, data, { headers });
        invalidateAuthenticatedResource('payment-billing-history');
        return res.data;
    },
};
